import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_ALL_SUBCATEGORIES } from "src/graphql/query";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}

const MultiSubCategorySelector: React.FC<Props> = ({
  selectedIds,
  setSelectedIds,
}) => {
  const { data, loading, error } = useQuery(GET_ALL_SUBCATEGORIES);

  const [subcategories, setSubcategories] = useState<
    { _id: string; subCategoryName: string }[]
  >([]);

  const [open, setOpen] = useState(false); // for controlling dropdown open/close

  useEffect(() => {
    if (data?.getAllSubCategories?.data) {
      setSubcategories(data?.getAllSubCategories?.data);
    }
  }, [data]);
  
  const handleSelect = (event: any) => {
    const {
      target: { value },
    } = event;
    const newId = value[value.length - 1];
    if (!selectedIds.includes(newId)) {
      setSelectedIds([...selectedIds, newId]);
    }
    setOpen(false); // close after selection
  };

  const handleDelete = (idToDelete: string) => {
    setSelectedIds(selectedIds.filter((id) => id !== idToDelete));
  };

  const availableOptions = subcategories.filter(
    (sub) => !selectedIds.includes(sub._id)
  );

  if (loading) return <CircularProgress size={20} />;
  if (error) return <Typography color="error">Failed to load subcategories</Typography>;

  return (
    <Box>
      <Select
        multiple
        fullWidth
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={handleSelect}
        value={[]}
        input={<OutlinedInput />}
        renderValue={() => null}
        displayEmpty
      >
        {availableOptions.map((sub) => (
          <MenuItem key={sub._id} value={sub._id}>
            {sub.subCategoryName}
          </MenuItem>
        ))}
      </Select>

      <Box mt={1} sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {selectedIds.map((id) => {
          const subcat = subcategories.find((s) => s._id === id);
          return (
            <Chip
              key={id}
              label={subcat?.subCategoryName || "Unknown"}
              onDelete={() => handleDelete(id)}
              deleteIcon={<CloseIcon />}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default MultiSubCategorySelector;

